import { alert } from '@/services/alert';
import { ActionIcon, Avatar, Box, Button, Group, Menu, ScrollArea, Stack, Text, TextInput } from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { getReferenceString, parseReference, resolveId } from '@medplum/core';
import { DocumentReference, Patient, Practitioner, Reference } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import {
  IconCalendarStats,
  IconCheck,
  IconChevronDown,
  IconFilter,
  IconPlus,
  IconSearch,
  IconTags,
  IconUserCircle,
} from '@tabler/icons-react';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { UploadDocumentModal } from './UploadDocumentModal';

type SearchAndFiltersProps = {
  documents: DocumentReference[];
  onSearchChange: (search: string) => void;
  onUserFilterChange: (userId: string | null) => void;
  onDateFilterChange: (date: Date | null) => void;
  onTypeFilterChange: (type: string | null) => void;
  patient?: Patient;
  practitioner?: Practitioner;
  onUploadSuccess?: () => void;
};

export function SearchAndFilters({
  documents,
  onSearchChange,
  onUserFilterChange,
  onDateFilterChange,
  onTypeFilterChange,
  patient,
  practitioner,
  onUploadSuccess,
}: SearchAndFiltersProps): ReactElement {
  const medplum = useMedplum();
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [practitioners, setPractitioners] = useState<Map<string, Practitioner>>(new Map());
  const [uploadModalOpened, setUploadModalOpened] = useState(false);

  // Get unique user references from documents (memoized)
  const uniqueUserRefs = useMemo(() => {
    const refMap = new Map<string, Reference<Practitioner>>();

    for (const doc of documents) {
      const authorRef = doc.author?.[0];
      if (!authorRef) continue;

      // Type guard: ensure it's a Practitioner reference
      const [resourceType] = parseReference(authorRef);
      if (resourceType !== 'Practitioner') continue;

      const refString = getReferenceString(authorRef);
      if (!refString) continue;

      if (!refMap.has(refString)) {
        refMap.set(refString, authorRef as Reference<Practitioner>);
      }
    }

    return Array.from(refMap.values());
  }, [documents]);

  // Fetch practitioners
  useEffect(() => {
    const fetchPractitioners = async () => {
      try {
        const practitionerIds = uniqueUserRefs
          .map((ref) => {
            const [resourceType, id] = parseReference(ref);
            return resourceType === 'Practitioner' ? id : null;
          })
          .filter((id): id is string => !!id);

        if (practitionerIds.length === 0) {
          setPractitioners(new Map());
          return;
        }

        const practitioners = await medplum.searchResources('Practitioner', {
          _id: practitionerIds.join(','),
        });

        const practitionerMap = new Map<string, Practitioner>();
        for (const practitioner of practitioners) {
          if (practitioner.id) {
            practitionerMap.set(practitioner.id, practitioner);
          }
        }

        setPractitioners(practitionerMap);
      } catch (error) {
        alert.error(`Couldn't fetch practitioners: ${error instanceof Error ? error.message : 'Unknown error'}`);
        setPractitioners(new Map());
      }
    };

    if (uniqueUserRefs.length > 0) {
      fetchPractitioners();
    }
  }, [medplum, uniqueUserRefs]);

  // Get unique users with their info (memoized)
  const uniqueUsers = useMemo(() => {
    const userMap = new Map<string, { id: string; display: string }>();

    for (const doc of documents) {
      const authorRef = doc.author?.[0];
      if (!authorRef) continue;

      const ref = getReferenceString(authorRef);
      const display = authorRef.display;
      if (!ref || !display) continue;

      const practitionerId = resolveId(authorRef as Reference<Practitioner>);
      if (!practitionerId) continue;

      // Map automatically handles deduplication by key (id)
      if (!userMap.has(practitionerId)) {
        userMap.set(practitionerId, { id: practitionerId, display });
      }
    }

    return Array.from(userMap.values());
  }, [documents]);

  // Get unique types from documents (memoized)
  const uniqueTypes = useMemo(() => {
    const typeSet = new Set<string>();

    for (const doc of documents) {
      const contentType = doc.content?.[0]?.attachment?.contentType;
      if (!contentType) continue;

      const type = contentType.split('/')[1]?.toUpperCase() || contentType;
      typeSet.add(type);
    }

    return Array.from(typeSet);
  }, [documents]);

  const handleSearchChange = useCallback(
    (search: string) => {
      setSearchTerm(search);
      onSearchChange(search);
    },
    [onSearchChange]
  );

  const handleUserChange = useCallback(
    (userId: string | null) => {
      setSelectedUser(userId);
      onUserFilterChange(userId);
    },
    [onUserFilterChange]
  );

  const handleDateChange = useCallback(
    (date: string | Date | null) => {
      let dateObj: Date | null = null;
      if (date) {
        if (date instanceof Date) {
          dateObj = date;
        } else if (typeof date === 'string') {
          const [year, month, day] = date.split('-').map(Number);
          dateObj = new Date(year, month - 1, day);
        }
      }
      setSelectedDate(dateObj);
      onDateFilterChange(dateObj);
    },
    [onDateFilterChange]
  );

  const handleTypeChange = useCallback(
    (type: string | null) => {
      setSelectedType(type);
      onTypeFilterChange(type);
    },
    [onTypeFilterChange]
  );

  return (
    <>
      <Group gap="xs" px="4px">
        <TextInput
          placeholder="Search"
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.currentTarget.value)}
          leftSection={<IconSearch size={16} />}
          flex={1}
          styles={{
            input: {
              borderColor: 'var(--mantine-color-gray-3)',
            },
          }}
          radius="md"
        />
        <ActionIcon variant="subtle" size="lg" radius="sm" c="black" onClick={() => setShowFilters(!showFilters)}>
          <IconFilter size={18} style={{ transform: 'scaleX(-1)' }} />
        </ActionIcon>
        <ActionIcon
          variant="subtle"
          size="lg"
          radius="sm"
          c="black"
          onClick={() => setUploadModalOpened(true)}
          title="Upload documents"
        >
          <IconPlus size={18} style={{ transform: 'scaleX(-1)' }} />
        </ActionIcon>
      </Group>

      {showFilters && (
        <Group
          gap="xl"
          px="md"
          py="sm"
          mb="md"
          bg="gray.0"
          style={{
            borderTop: '1px solid var(--mantine-color-gray-3)',
            borderBottom: '1px solid var(--mantine-color-gray-3)',
          }}
        >
          <Menu withinPortal position="bottom-start" offset={8}>
            <Menu.Target>
              <Group gap={6} style={{ cursor: 'pointer' }}>
                <IconUserCircle size={16} />
                <Text size="sm">User</Text>
                <IconChevronDown size={16} />
              </Group>
            </Menu.Target>
            <Menu.Dropdown style={{ borderRadius: 16, padding: 4, minWidth: 250 }}>
              <Box p="md">
                <TextInput
                  placeholder="Search"
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.currentTarget.value)}
                  leftSection={<IconSearch size={16} />}
                  size="sm"
                  radius="lg"
                />
              </Box>
              <ScrollArea.Autosize mah={300}>
                <Menu.Item
                  onClick={() => handleUserChange(null)}
                  rightSection={
                    selectedUser === null ? <IconCheck size={16} color="var(--mantine-color-blue-6)" /> : null
                  }
                >
                  All users
                </Menu.Item>
                <Menu.Divider />
                {uniqueUsers
                  .filter((user) => user.display.toLowerCase().includes(userSearchTerm.toLowerCase()))
                  .map((user) => {
                    const practitioner = practitioners.get(user.id);
                    const photoUrl = practitioner?.photo?.[0]?.url;

                    return (
                      <Menu.Item
                        key={user.id}
                        onClick={() => handleUserChange(user.id)}
                        rightSection={
                          selectedUser === user.id ? <IconCheck size={16} color="var(--mantine-color-blue-6)" /> : null
                        }
                        leftSection={
                          <Avatar src={photoUrl} size="sm" radius="xl">
                            {!photoUrl && user.display.charAt(0).toUpperCase()}
                          </Avatar>
                        }
                      >
                        {user.display}
                      </Menu.Item>
                    );
                  })}
              </ScrollArea.Autosize>
            </Menu.Dropdown>
          </Menu>
          <Menu withinPortal position="bottom-start" offset={8}>
            <Menu.Target>
              <Group gap={6} style={{ cursor: 'pointer' }}>
                <IconCalendarStats size={16} />
                <Text size="sm">Date</Text>
                <IconChevronDown size={16} />
              </Group>
            </Menu.Target>
            <Menu.Dropdown style={{ borderRadius: 16, padding: 4, minWidth: 250 }}>
              <Stack gap="xs" p="md">
                <DatePicker
                  value={selectedDate}
                  onChange={(date) => handleDateChange(date as Date | null)}
                  size="xs"
                  styles={{
                    calendarHeaderLevel: {
                      fontWeight: 700,
                    },
                  }}
                />
                {selectedDate && (
                  <Button variant="subtle" size="xs" onClick={() => handleDateChange(null)} fullWidth>
                    Clear date
                  </Button>
                )}
              </Stack>
            </Menu.Dropdown>
          </Menu>
          <Menu withinPortal position="bottom-start" offset={8}>
            <Menu.Target>
              <Group gap={6} style={{ cursor: 'pointer' }}>
                <IconTags size={16} />
                <Text size="sm">Type</Text>
                <IconChevronDown size={16} />
              </Group>
            </Menu.Target>
            <Menu.Dropdown style={{ borderRadius: 16, padding: 4, minWidth: 250 }}>
              <ScrollArea.Autosize mah={300}>
                <Menu.Item
                  onClick={() => handleTypeChange(null)}
                  rightSection={
                    selectedType === null ? <IconCheck size={16} color="var(--mantine-color-blue-6)" /> : null
                  }
                >
                  All types
                </Menu.Item>
                <Menu.Divider />
                {uniqueTypes.map((type) => (
                  <Menu.Item
                    key={type}
                    onClick={() => handleTypeChange(type)}
                    rightSection={
                      selectedType === type ? <IconCheck size={16} color="var(--mantine-color-blue-6)" /> : null
                    }
                  >
                    {type}
                  </Menu.Item>
                ))}
              </ScrollArea.Autosize>
            </Menu.Dropdown>
          </Menu>
        </Group>
      )}

      {patient && practitioner && (
        <UploadDocumentModal
          opened={uploadModalOpened}
          onClose={() => setUploadModalOpened(false)}
          onUploadSuccess={() => {
            onUploadSuccess?.();
            setUploadModalOpened(false);
          }}
          patient={patient}
          practitioner={practitioner}
        />
      )}
    </>
  );
}
